/**
 * BetUDontBet Poker — BUDB Slots
 * Layered cabinet art + craftpix chrome + CSS reel-strip spin animation.
 */
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type RefObject,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { useWalletState } from "@/contexts/wallet-context";
import { useToast } from "@/hooks/use-toast";
import { useDappSign } from "@/contexts/dapp-sign-context";
import { playSessionHeaders } from "@/lib/play-session";
import {
  SlotSymbol,
  SYMBOL_LABELS,
  JackpotTierBadge,
  type SymbolId,
} from "@/components/slot-symbols";
import { BRAND } from "@shared/brand";
import {
  JACKPOT_ORDER,
  JACKPOT_TIERS,
  FREE_SPIN_BOUNTY,
  REEL_COUNT,
  ROW_COUNT,
  MAX_LINES,
  PAYLINES,
  PAYLINE_COLORS,
  SLOT_MATH,
  getPaytable,
  getSlotMathDisclosure,
  type JackpotTierId,
} from "@shared/slot-engine";
import {
  ChevronLeft,
  Info,
  Maximize2,
  Minimize2,
  Smartphone,
} from "lucide-react";
import "@/styles/slots.css";
import { slotsAudio } from "@/lib/slots-audio";
import { SpriteFont, SpriteWinAmount } from "@/components/sprite-font";
import { preloadSevensFont } from "@/lib/sprite-fonts";
import {
  fetchPublishedLayouts,
  resolveSlotsLayout,
  slotsLayoutStyle,
} from "@/lib/layout-runtime";
import {
  FundPlayDialog,
  PlaySessionStatusLine,
} from "@/components/play-ledger-dialogs";
import { WantedGraphicalNovel } from "@/components/slots-wanted-novel";
import {
  emptyWantedHits,
  getLeftRailOpenPref,
  SlotsLeftRail,
  type WantedHitStats,
} from "@/components/slots-left-rail";

const JP_SYMBOL_IDS = new Set<string>(JACKPOT_ORDER);

/** Base sequential stops (ms) — last reel stretches on anticipation */
const BASE_STOP_DELAYS = [1050, 1600, 2200, 2900, 3650] as const;
/** Settle time after last reel begins stopping (strip ease ~0.78s) */
const REEL_LAND_SETTLE_MS = 820;

type AnticipationKind = "free" | "bonus" | "wanted" | "features";
type AnticipationPlan = {
  stopDelays: number[];
  level: 0 | 1 | 2 | 3;
  kind: AnticipationKind | null;
  label: string | null;
  hotReels: number[];
  /** ms from visual spin start → show anticipation banner */
  bannerAt: number;
};

type WantedFlyer = {
  id: string;
  tier: JackpotTierId;
  poster: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  delay: number;
};

function countSymbolOnReels(
  grid: string[][],
  fromReel: number,
  toReel: number,
  id: string,
): number {
  let n = 0;
  for (let r = fromReel; r <= toReel; r++) {
    for (const s of grid[r] ?? []) {
      if (s === id) n += 1;
    }
  }
  return n;
}

/**
 * Video-slots anticipation: when 2+ feature/Wanted already sit on earlier reels,
 * stretch the last (and sometimes 4th) reel — bigger features = longer tease.
 */
function planReelStops(
  grid: string[][],
  result: {
    freeSpinsTriggered?: boolean;
    bonusTriggered?: boolean;
    jackpotAwards?: JackpotAward[];
  },
): AnticipationPlan {
  const delays: number[] = [...BASE_STOP_DELAYS];
  let level: 0 | 1 | 2 | 3 = 0;
  let kind: AnticipationKind | null = null;
  let label: string | null = null;
  const hot: number[] = [];
  const fourth = REEL_COUNT - 2;
  const last = REEL_COUNT - 1;

  const scatter02 = countSymbolOnReels(grid, 0, 2, "scatter");
  const bonus02 = countSymbolOnReels(grid, 0, 2, "bonus");
  const scatter03 = countSymbolOnReels(grid, 0, 3, "scatter");
  const bonus03 = countSymbolOnReels(grid, 0, 3, "bonus");

  let bestJp02 = 0;
  let bestJp03 = 0;
  let bestJpTier: JackpotTierId | null = null;
  for (const tier of JACKPOT_ORDER) {
    const c02 = countSymbolOnReels(grid, 0, 2, tier);
    const c03 = countSymbolOnReels(grid, 0, 3, tier);
    if (c02 > bestJp02) bestJp02 = c02;
    if (c03 >= bestJp03) {
      bestJp03 = c03;
      bestJpTier = tier;
    }
  }

  let extraFourth = 0;
  let extraLast = 0;

  // Early tease: 2 already on reels 1–3 → reel 4 hangs a bit
  if (scatter02 >= 2 || bonus02 >= 2 || bestJp02 >= 2) {
    extraFourth = 700;
    level = 1;
    hot.push(fourth);
    if (scatter02 >= 2) {
      kind = "free";
      label = "MALACHAR NEAR…";
    } else if (bonus02 >= 2) {
      kind = "bonus";
      label = "VAULT NEAR…";
    } else {
      kind = "wanted";
      label = "WANTED NEAR…";
    }
  }

  // Main beat: 2+ on first four reels → last reel drama
  if (scatter03 >= 2) {
    extraLast = Math.max(extraLast, 1800);
    level = Math.max(level, 2) as 0 | 1 | 2 | 3;
    kind = kind === "bonus" ? "features" : "free";
    label = scatter03 >= 3 ? "SEA VOYAGE!" : "WILL MALACHAR HIT?";
  }
  if (bonus03 >= 2) {
    extraLast = Math.max(extraLast, 2000);
    level = Math.max(level, 2) as 0 | 1 | 2 | 3;
    kind = kind === "free" || kind === "features" ? "features" : "bonus";
    label = bonus03 >= 3 ? "ETHEREAL VAULT!" : "WILL THE SEAL LAND?";
  }
  if (bestJp03 >= 2 && bestJpTier) {
    const t = JACKPOT_TIERS[bestJpTier];
    const jpExtra = t.mult >= 50 ? 2900 : t.mult >= 18 ? 2300 : 1650;
    extraLast = Math.max(extraLast, jpExtra);
    level = Math.max(level, t.mult >= 50 ? 3 : 2) as 0 | 1 | 2 | 3;
    if (!label || t.mult >= 50) {
      kind = kind && kind !== "wanted" ? "features" : "wanted";
      label =
        bestJp03 >= 3
          ? `${t.name.toUpperCase()} CLAIMED!`
          : `${t.name.toUpperCase()}…?`;
    }
  }

  // Confirmed hits (server) — max excitement even if borderline
  if (result.freeSpinsTriggered) {
    extraLast = Math.max(extraLast, 2200);
    level = Math.max(level, 2) as 0 | 1 | 2 | 3;
    kind = kind === "bonus" || kind === "features" ? "features" : "free";
    label = "SEA VOYAGE INCOMING!";
  }
  if (result.bonusTriggered) {
    extraLast = Math.max(extraLast, 2500);
    level = Math.max(level, 3) as 0 | 1 | 2 | 3;
    kind = kind === "free" || kind === "features" ? "features" : "bonus";
    label = "ETHEREAL VAULT OPENING!";
  }
  if (result.jackpotAwards?.length) {
    const top = result.jackpotAwards.reduce((a, b) =>
      (b.mult ?? 0) > (a.mult ?? 0) ? b : a,
    );
    extraLast = Math.max(extraLast, (top.mult ?? 0) >= 50 ? 3400 : 2500);
    level = Math.max(level, 3) as 0 | 1 | 2 | 3;
    kind = "wanted";
    label = `${top.name.toUpperCase()} BOUNTY!`;
  }

  // Preserve sequential order: last always after fourth
  delays[fourth] = BASE_STOP_DELAYS[fourth]! + extraFourth;
  const baseGap = BASE_STOP_DELAYS[last]! - BASE_STOP_DELAYS[fourth]!;
  delays[last] = delays[fourth]! + baseGap + extraLast;

  if (extraLast > 0) hot.push(last);
  const hotReels = [...new Set(hot)];
  const firstHot = hotReels.length ? Math.min(...hotReels) : last;
  const bannerAt =
    firstHot > 0
      ? delays[firstHot - 1]! + 60
      : Math.floor(delays[0]! * 0.55);

  return {
    stopDelays: delays,
    level,
    kind: level > 0 ? kind : null,
    label: level > 0 ? label : null,
    hotReels,
    bannerAt,
  };
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface WildCell {
  reel: number;
  row: number;
  multiplier: number;
}
interface PaylinePos {
  reel: number;
  row: number;
}
interface PaylineResult {
  paylineIndex: number;
  symbolId: string;
  matchCount: number;
  win: number;
  wildMultiplier: number;
  positions: PaylinePos[];
}
interface JackpotAward {
  tier: JackpotTierId;
  name: string;
  count: number;
  win: number;
  mult: number;
}
interface SpinResult {
  handId: string;
  reelGrid: string[][];
  wildCells: WildCell[];
  paylineResults: PaylineResult[];
  scatterCount: number;
  scatterPositions: PaylinePos[];
  bonusCount?: number;
  bonusPositions?: PaylinePos[];
  totalWin: number;
  totalBet: number;
  multiplier: number;
  isJackpot: boolean;
  isBigWin: boolean;
  freeSpinsTriggered: boolean;
  freeSpinsAwarded: number;
  freeSpinsRemaining: number;
  freeSpinsMultiplier: number;
  isFreeSpinMode: boolean;
  bonusTriggered?: boolean;
  bonusSpinsAwarded?: number;
  bonusSpinsRemaining?: number;
  bonusSpinsMultiplier?: number;
  isBonusMode?: boolean;
  isSpecialSpin?: boolean;
  specialSpinTriggered?: boolean;
  specialSpinsRemaining?: number;
  jackpotAwards?: JackpotAward[];
  jackpotWin?: number;
  spinKind?: "base" | "free" | "bonus" | "special";
}

const CELL_PX = 56;
/** Longer blur strip = more “spin” feel (Classic Sevens density) */
const STRIP_SPIN_LEN = 36;
const BET_PRESETS = [0.1, 0.5, 1, 2, 5, 10];
const LINE_PRESETS = [5, 10, 15, 20, 25];
/** CraftPix Classic Sevens chrome */
const CRAFTPIX_SPIN = "/slots/craftpix/buttons/Spin1.png";
const CRAFTPIX_SPIN_PRESS = "/slots/craftpix/buttons/Spin3.png";
const CRAFTPIX_WIN_RING = "/slots/craftpix/frames/Frame10.png";
const SPIN_SYMBOLS: SymbolId[] = [
  "skull",
  "gbux",
  "crown",
  "diamond",
  "wolf",
  "ace",
  "dice",
  "revolver",
  "wild",
  "scatter",
  "bonus",
  "jp_mini",
  "jp_medium",
  "jp_large",
  "jp_grand",
];

function randomSymbol(): SymbolId {
  return SPIN_SYMBOLS[Math.floor(Math.random() * SPIN_SYMBOLS.length)]!;
}

/** Attractive idle showcase (not a wall of one symbol) */
function makeDefaultGrid(): string[][] {
  const seed: SymbolId[][] = [
    ["skull", "crown", "gbux", "diamond", "wolf"],
    ["dice", "wild", "ace", "revolver", "skull"],
    ["crown", "gbux", "scatter", "diamond", "dice"],
    ["wolf", "ace", "revolver", "bonus", "crown"],
    ["gbux", "skull", "diamond", "dice", "jp_mini"],
  ];
  return seed.map((col) => col.slice(0, ROW_COUNT));
}

function AmbientDust({ count = 18 }: { count?: number }) {
  const motes = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: `${(i * 17 + 5) % 100}%`,
        delay: `${(i % 10) * 0.7}s`,
        dur: `${8 + (i % 6) * 1.4}s`,
        size: 2 + (i % 4),
      })),
    [count],
  );
  return (
    <div className="slots-dust" aria-hidden>
      {motes.map((m) => (
        <span
          key={m.id}
          className="slots-dust-mote"
          style={{
            left: m.left,
            animationDelay: m.delay,
            animationDuration: m.dur,
            width: m.size,
            height: m.size,
          }}
        />
      ))}
    </div>
  );
}

function WinSparks({ active }: { active: boolean }) {
  const sparks = useMemo(
    () =>
      Array.from({ length: 16 }, (_, i) => ({
        id: i,
        left: `${10 + (i * 5.5) % 80}%`,
        top: `${15 + ((i * 11) % 70)}%`,
        sx: `${(i % 2 === 0 ? 1 : -1) * (12 + (i % 7) * 8)}px`,
        sy: `${-18 - (i % 5) * 10}px`,
        delay: `${(i % 8) * 0.04}s`,
      })),
    [],
  );
  if (!active) return null;
  return (
    <div className="slots-spark-layer" aria-hidden>
      {sparks.map((s) => (
        <span
          key={s.id}
          className="slots-spark"
          style={{
            left: s.left,
            top: s.top,
            animationDelay: s.delay,
            ["--sx" as string]: s.sx,
            ["--sy" as string]: s.sy,
          }}
        />
      ))}
    </div>
  );
}

/** Cell centers as % of reel-window — accounts for padding + reel gap (industry glass layout). */
type ReelGeo = {
  centers: Array<Array<{ x: number; y: number }>>;
};

function useReelGeometry(
  windowRef: RefObject<HTMLElement | null>,
  measureKey: number,
): ReelGeo | null {
  const [geo, setGeo] = useState<ReelGeo | null>(null);

  useEffect(() => {
    const el = windowRef.current;
    if (!el) return;
    const measure = () => {
      const wr = el.getBoundingClientRect();
      if (wr.width < 8 || wr.height < 8) return;
      const reels = el.querySelectorAll<HTMLElement>(".slots-reel");
      if (reels.length < REEL_COUNT) return;
      const centers: Array<Array<{ x: number; y: number }>> = [];
      reels.forEach((reelEl) => {
        const rr = reelEl.getBoundingClientRect();
        const cellH = rr.height / ROW_COUNT;
        const col: Array<{ x: number; y: number }> = [];
        for (let row = 0; row < ROW_COUNT; row++) {
          col.push({
            x: ((rr.left - wr.left + rr.width / 2) / wr.width) * 100,
            y: ((rr.top - wr.top + cellH * row + cellH / 2) / wr.height) * 100,
          });
        }
        centers.push(col);
      });
      if (centers.length === REEL_COUNT) setGeo({ centers });
    };
    measure();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(measure);
    });
    ro.observe(el);
    window.addEventListener("orientationchange", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", measure);
    };
  }, [windowRef, measureKey]);

  return geo;
}

/**
 * Winning paylines — full 5-point path (catalog pattern) + match segment glow.
 * Cycles active line blink (Pragmatic / NetEnt-style presentation).
 */
function PaylineOverlay({
  results,
  windowRef,
  measureKey,
  activeIndex,
}: {
  results: PaylineResult[];
  windowRef: RefObject<HTMLElement | null>;
  measureKey: number;
  activeIndex: number;
}) {
  const geo = useReelGeometry(windowRef, measureKey);
  if (!results.length || !geo) return null;

  const active = results[activeIndex % results.length];

  const pathFor = (paylineIndex: number, onlyMatch?: PaylinePos[]) => {
    const pattern = PAYLINES[paylineIndex];
    if (!pattern) return "";
    if (onlyMatch?.length) {
      return onlyMatch
        .map((p) => {
          const c = geo.centers[p.reel]?.[p.row];
          return c ? `${c.x},${c.y}` : null;
        })
        .filter(Boolean)
        .join(" ");
    }
    return pattern
      .map((row, reel) => {
        const c = geo.centers[reel]?.[row];
        return c ? `${c.x},${c.y}` : null;
      })
      .filter(Boolean)
      .join(" ");
  };

  return (
    <svg
      className="slots-payline-svg"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden
    >
      {/* Dim ghosts of all wins this spin */}
      {results.map((r, i) => {
        if (i === activeIndex % results.length) return null;
        const color =
          PAYLINE_COLORS[r.paylineIndex % PAYLINE_COLORS.length] ?? "#fbbf24";
        const pts = pathFor(r.paylineIndex);
        if (!pts) return null;
        return (
          <polyline
            key={`ghost-${r.paylineIndex}-${i}`}
            points={pts}
            fill="none"
            stroke={color}
            strokeWidth="1.1"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="slots-payline-ghost"
            opacity={0.28}
          />
        );
      })}
      {active ? (
        <>
          {/* Full catalog line path */}
          <polyline
            key={`full-${active.paylineIndex}-${activeIndex}`}
            points={pathFor(active.paylineIndex)}
            fill="none"
            stroke={
              PAYLINE_COLORS[active.paylineIndex % PAYLINE_COLORS.length] ??
              "#fbbf24"
            }
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="slots-payline-stroke slots-payline-stroke--active"
          />
          {/* Thick blink on matched segment only */}
          <polyline
            key={`match-${active.paylineIndex}-${activeIndex}`}
            points={pathFor(active.paylineIndex, active.positions)}
            fill="none"
            stroke="#fff8dc"
            strokeWidth="3.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="slots-payline-stroke slots-payline-stroke--match"
          />
          {active.positions.map((p, i) => {
            const c = geo.centers[p.reel]?.[p.row];
            if (!c) return null;
            return (
              <circle
                key={`dot-${p.reel}-${p.row}-${i}`}
                cx={c.x}
                cy={c.y}
                r="2.4"
                className="slots-payline-dot"
                fill={
                  PAYLINE_COLORS[active.paylineIndex % PAYLINE_COLORS.length] ??
                  "#fbbf24"
                }
              />
            );
          })}
        </>
      ) : null}
    </svg>
  );
}

/** Viewport mode: phone / landscape / desktop + browser fullscreen */
function useSlotsDisplayMode() {
  const [mode, setMode] = useState<"phone" | "landscape" | "desktop">("desktop");
  const [isFs, setIsFs] = useState(false);

  useEffect(() => {
    const read = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const landscape = w > h;
      const short = Math.min(w, h) < 520 || h < 500;
      if (landscape && (h <= 560 || short)) setMode("landscape");
      else if (w <= 768) setMode("phone");
      else setMode("desktop");
      setIsFs(Boolean(document.fullscreenElement));
    };
    read();
    window.addEventListener("resize", read);
    window.addEventListener("orientationchange", read);
    document.addEventListener("fullscreenchange", read);
    return () => {
      window.removeEventListener("resize", read);
      window.removeEventListener("orientationchange", read);
      document.removeEventListener("fullscreenchange", read);
    };
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        const root = document.querySelector(".slots-page") as HTMLElement | null;
        await (root ?? document.documentElement).requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      /* browser may block — CSS immersive still works */
    }
  }, []);

  return { mode, isFs, toggleFullscreen };
}

// ── Animated reel (strip + CSS) ───────────────────────────────────────────────

interface ReelProps {
  symbols: string[];
  wildRows: WildCell[];
  winRows: number[];
  spinning: boolean;
  stopDelay: number;
  spinToken: number;
  isFreeSpins: boolean;
  /** Longer land ease when this reel is a drama stop */
  anticipation?: boolean;
  anticipationLevel?: 0 | 1 | 2 | 3;
  /** Gold pulse only after earlier reels have landed (tease window) */
  anticipationGlow?: boolean;
  /** Fires the instant this reel begins its land ease */
  onLand?: () => void;
}

function AnimatedReel({
  symbols,
  wildRows,
  winRows,
  spinning,
  stopDelay,
  spinToken,
  isFreeSpins,
  anticipation = false,
  anticipationLevel = 0,
  anticipationGlow = false,
  onLand,
}: ReelProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const [strip, setStrip] = useState<string[]>(() => [...symbols]);
  const [phase, setPhase] = useState<"idle" | "spinning" | "stopping">("idle");
  const [offsetPx, setOffsetPx] = useState(0);
  const [cellH, setCellH] = useState(CELL_PX);
  const [justLanded, setJustLanded] = useState(false);
  const cellHRef = useRef(CELL_PX);
  const onLandRef = useRef(onLand);
  onLandRef.current = onLand;
  const landEaseMs = anticipation
    ? anticipationLevel >= 3
      ? 1050
      : 920
    : 780;

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => {
      const h = Math.max(40, el.clientHeight / ROW_COUNT);
      cellHRef.current = h;
      setCellH(h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!spinning || spinToken === 0) return;

    const blur: string[] = [];
    for (let i = 0; i < STRIP_SPIN_LEN; i++) blur.push(randomSymbol());
    const full = [...blur, ...symbols];
    setStrip(full);
    setPhase("spinning");
    setOffsetPx(0);
    setJustLanded(false);

    const stopT = window.setTimeout(() => {
      const ch = cellHRef.current;
      const finalTopIndex = full.length - ROW_COUNT;
      const target = finalTopIndex * ch;
      setPhase("stopping");
      setJustLanded(true);
      requestAnimationFrame(() => setOffsetPx(target));
      try {
        slotsAudio.reelTick();
      } catch {
        /* optional */
      }
      try {
        onLandRef.current?.();
      } catch {
        /* parent fly-out */
      }
      window.setTimeout(() => {
        setStrip([...symbols]);
        setOffsetPx(0);
        setPhase("idle");
        window.setTimeout(() => setJustLanded(false), 480);
      }, landEaseMs);
    }, stopDelay);

    return () => clearTimeout(stopT);
  }, [spinning, spinToken, stopDelay, symbols, landEaseMs]);

  useEffect(() => {
    if (phase === "idle" && !spinning) {
      setStrip([...symbols]);
      setOffsetPx(0);
    }
  }, [symbols, spinning, phase]);

  const transform = phase === "spinning" ? undefined : `translateY(-${offsetPx}px)`;

  return (
    <div
      ref={viewportRef}
      className={`slots-reel ${
        phase === "stopping" || justLanded ? "is-stopping" : ""
      } ${anticipationGlow && phase === "spinning" ? "is-anticipation" : ""} ${
        anticipationGlow &&
        anticipationLevel >= 3 &&
        phase === "spinning"
          ? "is-anticipation-max"
          : ""
      }`}
      data-free={isFreeSpins || undefined}
      style={{ ["--cell-h" as string]: `${cellH}px` }}
    >
      <div
        ref={stripRef}
        className={`slots-reel-strip ${phase === "spinning" ? "spinning" : ""} ${
          phase === "stopping" ? "stopping" : ""
        } ${anticipation && phase === "stopping" ? "stopping-anticipation" : ""}`}
        style={
          {
            transform,
            ["--cell-h" as string]: `${cellH}px`,
            ["--land-ease" as string]: `${landEaseMs}ms`,
          } as CSSProperties
        }
      >
        {strip.map((sym, i) => {
          const rowInFinal =
            phase === "idle" ? i : i - (strip.length - ROW_COUNT);
          const isWin =
            phase === "idle" &&
            rowInFinal >= 0 &&
            rowInFinal < ROW_COUNT &&
            winRows.includes(rowInFinal);
          const showLand =
            justLanded &&
            phase !== "spinning" &&
            rowInFinal >= 0 &&
            rowInFinal < ROW_COUNT;
          const wc =
            phase === "idle"
              ? wildRows.find((w) => w.row === rowInFinal)
              : undefined;
          return (
            <div
              key={`${spinToken}-${i}-${sym}`}
              className={`slots-cell ${isWin ? "win" : ""} ${showLand ? "land-pop" : ""}`}
              style={{ height: cellH }}
            >
              <SlotSymbol
                id={sym as SymbolId}
                wildMultiplier={wc?.multiplier}
                isWinning={isWin}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── FX overlays (AI-generated stills + motion) ───────────────────────────────

function CoinRain({ count = 28 }: { count?: number }) {
  const coins = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: `${(i * 37 + 11) % 100}%`,
        delay: `${(i % 12) * 0.18}s`,
        dur: `${2.4 + (i % 7) * 0.35}s`,
        size: 12 + (i % 5) * 4,
      })),
    [count],
  );
  return (
    <div className="slots-coin-rain" aria-hidden>
      {coins.map((c) => (
        <span
          key={c.id}
          className="slots-coin"
          style={{
            left: c.left,
            animationDelay: c.delay,
            animationDuration: c.dur,
            width: c.size,
            height: c.size,
          }}
        />
      ))}
    </div>
  );
}

function FxSplash({
  bg,
  title,
  sub,
  badge,
  onDismiss,
  autoMs = 4800,
  coins = true,
}: {
  bg: string;
  title: string;
  sub?: string;
  badge?: string;
  onDismiss: () => void;
  autoMs?: number;
  coins?: boolean;
}) {
  useEffect(() => {
    const t = setTimeout(onDismiss, autoMs);
    return () => clearTimeout(t);
  }, [onDismiss, autoMs]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="slots-fx-overlay"
      onClick={onDismiss}
      role="dialog"
      aria-label={title}
    >
      <div className="slots-fx-bg" style={{ backgroundImage: `url(${bg})` }} />
      {coins && <CoinRain />}
      <motion.div
        className="slots-fx-content"
        initial={{ scale: 0.35, y: 60, rotate: -6 }}
        animate={{ scale: 1, y: 0, rotate: 0 }}
        transition={{ type: "spring", bounce: 0.55, duration: 0.7 }}
      >
        <div className="slots-fx-title">{title}</div>
        {sub && (
          <motion.div
            className="slots-fx-sub"
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 0.55, repeat: 6 }}
          >
            {sub}
          </motion.div>
        )}
        {badge && <div className="slots-fx-badge">{badge}</div>}
        <div className="slots-fx-hint">tap to continue</div>
      </motion.div>
    </motion.div>
  );
}

function FreeSpinTriggerOverlay({
  spins,
  multiplier,
  onDismiss,
}: {
  spins: number;
  multiplier: number;
  onDismiss: () => void;
}) {
  return (
    <FxSplash
      bg={BRAND.slots.fxFreeSpins}
      title="SEA VOYAGE"
      sub={`${spins} FREE SPINS`}
      badge={`RACALVIN · ${multiplier}×`}
      onDismiss={onDismiss}
      autoMs={4500}
    />
  );
}

function BigWinOverlay({
  win,
  isJackpot,
  onDismiss,
}: {
  win: number;
  isJackpot: boolean;
  onDismiss: () => void;
}) {
  return (
    <FxSplash
      bg={
        isJackpot
          ? BRAND.slots.fxJackpot
          : "/slots/fx/big-win-v5.jpg"
      }
      title={isJackpot ? "MOST WANTED!" : "BIG WIN!"}
      sub={`+${win.toLocaleString()} GBUX`}
      onDismiss={onDismiss}
      autoMs={isJackpot ? 5600 : 4800}
      coins
    />
  );
}

function BonusTriggerOverlay({
  spins,
  multiplier,
  onDismiss,
}: {
  spins: number;
  multiplier: number;
  onDismiss: () => void;
}) {
  return (
    <FxSplash
      bg={BRAND.slots.ambientAlt || BRAND.slots.fxBonus}
      title="ETHEREAL TREASURE"
      sub={`${spins} BONUS SPINS`}
      badge={`RACALVIN VAULT · ${multiplier}×`}
      onDismiss={onDismiss}
      autoMs={5000}
    />
  );
}

/** Intro cinematic — Racalvin voyage (sound after user gesture; BGM starts) */
function IntroVideoOverlay({ onDone }: { onDone: () => void }) {
  const [err, setErr] = useState(false);
  const [unmuted, setUnmuted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      slotsAudio.startBgm();
      onDone();
    }, 28000);
    return () => clearTimeout(t);
  }, [onDone]);

  const enableSoundAndEnter = () => {
    slotsAudio.unlock();
    slotsAudio.startBgm();
    slotsAudio.welcome();
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      v.volume = 0.85;
      void v.play().catch(() => {});
    }
    setUnmuted(true);
    onDone();
  };

  const tryUnmuteOnly = () => {
    slotsAudio.unlock();
    slotsAudio.startBgm();
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      v.volume = 0.85;
      void v.play().catch(() => {});
      setUnmuted(true);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="slots-fx-overlay"
      style={{ zIndex: 70, background: "#000" }}
    >
      {!err ? (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={BRAND.slots.introVideo}
          autoPlay
          muted={!unmuted}
          playsInline
          onEnded={() => {
            slotsAudio.startBgm();
            onDone();
          }}
          onError={() => setErr(true)}
        />
      ) : (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BRAND.slots.ambient})` }}
        />
      )}
      <div className="relative z-10 flex flex-col items-center justify-end h-full pb-16 px-4 text-center">
        <img
          src={BRAND.slots.logo}
          alt=""
          className="w-24 h-24 object-contain drop-shadow-2xl mb-3 rounded-xl"
        />
        <div className="slots-fx-title text-2xl sm:text-4xl">
          {BRAND.slots.title}
        </div>
        <div className="text-amber-200/90 text-sm tracking-widest uppercase mt-1">
          {BRAND.slots.subtitle}
        </div>
        {!unmuted && (
          <button
            type="button"
            className="mt-4 px-5 py-2 rounded-full border border-amber-400/50 text-amber-100 text-xs font-semibold"
            onClick={tryUnmuteOnly}
          >
            Enable sound
          </button>
        )}
        <button
          type="button"
          className="mt-3 px-6 py-2.5 rounded-full bg-amber-500 text-black font-bold text-sm shadow-lg"
          onClick={enableSoundAndEnter}
        >
          Enter the fleet
        </button>
      </div>
    </motion.div>
  );
}

/** Progressive sea conquest video strip */
function ProgressiveSeaBanner({
  sessionWon,
  sessionWager,
}: {
  sessionWon: number;
  sessionWager: number;
}) {
  const progress =
    sessionWager > 0
      ? Math.min(100, Math.round((sessionWon / Math.max(sessionWager, 1)) * 50 + 20))
      : 12;
  return (
    <div className="relative rounded-xl overflow-hidden border border-cyan-500/25 mb-2">
      <video
        className="absolute inset-0 w-full h-full object-cover opacity-50"
        src={BRAND.slots.progressiveVideo}
        autoPlay
        muted
        loop
        playsInline
      />
      <div className="relative z-10 px-3 py-2 bg-gradient-to-r from-black/75 via-black/40 to-black/70">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-cyan-100/90 mb-1">
          <span>Sea conquest progressive</span>
          <span>{progress}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-black/50 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-amber-400 transition-all duration-700"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-[10px] text-white/50 mt-1 truncate">
          Chart the ethereal sea — wins advance Racalvin&apos;s treasure route
        </p>
      </div>
    </div>
  );
}

interface PayEntry {
  id: string;
  name: string;
  pays3: number;
  pays4: number;
  pays5: number;
}

type InfoTab = "paytable" | "lines" | "math" | "privacy" | "terms";

function MiniPaylinePreview({
  pattern,
  color,
  active,
}: {
  pattern: number[];
  color: string;
  active?: boolean;
}) {
  const cell = 12;
  const gap = 2;
  const w = REEL_COUNT * cell + (REEL_COUNT - 1) * gap;
  const h = ROW_COUNT * cell + (ROW_COUNT - 1) * gap;
  const pts = pattern
    .map((row, reel) => {
      const x = reel * (cell + gap) + cell / 2;
      const y = row * (cell + gap) + cell / 2;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className={`slots-line-mini ${active ? "is-active" : ""}`}
      aria-hidden
    >
      {Array.from({ length: REEL_COUNT * ROW_COUNT }, (_, i) => {
        const reel = i % REEL_COUNT;
        const row = Math.floor(i / REEL_COUNT);
        return (
          <rect
            key={i}
            x={reel * (cell + gap)}
            y={row * (cell + gap)}
            width={cell}
            height={cell}
            rx={2}
            fill="rgba(255,255,255,0.06)"
          />
        );
      })}
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={active ? 2.4 : 1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SlotsInfoModal({
  onClose,
  onOpenNovel,
}: {
  onClose: () => void;
  onOpenNovel?: () => void;
}) {
  const [tab, setTab] = useState<InfoTab>("paytable");
  const [previewLine, setPreviewLine] = useState(0);
  const { data } = useQuery<{ payTable: PayEntry[] }>({
    queryKey: ["/api/slots/paytable"],
  });
  const payTable = data?.payTable?.length
    ? data.payTable
    : (getPaytable() as PayEntry[]);
  const math = getSlotMathDisclosure();

  const tabs: { id: InfoTab; label: string }[] = [
    { id: "paytable", label: "Paytable" },
    { id: "lines", label: "Paylines" },
    { id: "math", label: "Math" },
    { id: "privacy", label: "Privacy" },
    { id: "terms", label: "Terms" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="slots-info-overlay"
      onClick={onClose}
      role="presentation"
    >
      <motion.div
        initial={{ scale: 0.92, y: 28 }}
        animate={{ scale: 1, y: 0 }}
        className="slots-info-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal
        aria-label="Slots information"
      >
        <header className="slots-info-modal__head">
          <div>
            <h2>ETHEREAL TREASURE</h2>
            <p>
              {REEL_COUNT}×{ROW_COUNT} · {MAX_LINES} lines · L→R · RTP{" "}
              {(SLOT_MATH.targetRtp * 100).toFixed(1)}%
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <nav className="slots-info-tabs" aria-label="Info sections">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={tab === t.id ? "is-on" : ""}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="slots-info-body">
          {tab === "paytable" && (
            <>
              <div className="slots-info-paylist">
                {payTable.map((sym) => (
                  <div key={sym.id} className="slots-info-payrow">
                    <div className="slots-info-payrow__ico">
                      <SlotSymbol id={sym.id as SymbolId} />
                    </div>
                    <span className="slots-info-payrow__name">{sym.name}</span>
                    <div className="slots-info-payrow__pays">
                      <span>
                        3× <b>{sym.pays3}</b>
                      </span>
                      <span>
                        4× <b>{sym.pays4}</b>
                      </span>
                      <span>
                        5× <b>{sym.pays5}</b>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="slots-info-notes">
                <div className="slots-info-note">
                  <div className="w-8 h-8 shrink-0">
                    <SlotSymbol id="wild" />
                  </div>
                  <div>
                    <strong>Racalvin Wild</strong> — substitutes pay symbols ·
                    wild mult product cap {SLOT_MATH.wildMultCap}×
                  </div>
                </div>
                <div className="slots-info-note">
                  <div className="w-8 h-8 shrink-0">
                    <SlotSymbol id="bonus" />
                  </div>
                  <div>
                    <strong>Bonus seal</strong> — 3+ anywhere → bonus spins
                    (Most Wanted–weighted reels)
                  </div>
                </div>
                <div className="slots-info-note">
                  <img
                    src={FREE_SPIN_BOUNTY.icon}
                    alt=""
                    className="w-10 h-10 rounded object-cover shrink-0"
                  />
                  <div>
                    <strong>{FREE_SPIN_BOUNTY.name}</strong>
                    <div className="opacity-70 text-[11px]">
                      {FREE_SPIN_BOUNTY.title}
                    </div>
                    3 / 4 / 5 → 5 / 7 / 10 free spins
                  </div>
                </div>
                <div className="slots-info-wanted">
                  <div className="font-bold text-amber-200 mb-2">
                    Grudge&apos;s Most Wanted (3+ posters)
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {JACKPOT_ORDER.map((tier) => (
                      <JackpotTierBadge key={tier} tier={tier} />
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] opacity-55">
                    4 posters = 1.25× · 5 = 1.5× · 7+ = 2× · paid / vault /
                    special only (not free voyage).
                  </p>
                  <button
                    type="button"
                    className="mt-3 w-full rounded-lg border border-amber-500/40 bg-amber-950/40 py-2 text-xs font-bold tracking-widest text-amber-200 uppercase hover:bg-amber-900/50"
                    onClick={() => {
                      onClose();
                      onOpenNovel?.();
                    }}
                  >
                    Open Wanted graphical novel
                  </button>
                </div>
              </div>
            </>
          )}

          {tab === "lines" && (
            <div className="slots-info-lines">
              <p className="slots-info-lead">
                Up to <strong>{MAX_LINES}</strong> fixed paylines. Wins evaluate{" "}
                <strong>left → right</strong> from reel 1. Need{" "}
                <strong>3+</strong> matching pay symbols (or wilds). Scatter,
                bonus, and Most Wanted count <strong>anywhere</strong> — not on
                lines.
              </p>
              <div className="slots-info-line-focus">
                <MiniPaylinePreview
                  pattern={PAYLINES[previewLine]!}
                  color={
                    PAYLINE_COLORS[previewLine % PAYLINE_COLORS.length] ??
                    "#fbbf24"
                  }
                  active
                />
                <div>
                  <div className="text-amber-200 font-bold">
                    Line {previewLine + 1} / {PAYLINES.length}
                  </div>
                  <div className="text-[11px] opacity-60 font-mono">
                    rows [{PAYLINES[previewLine]?.join(", ")}]
                  </div>
                  <div
                    className="mt-1 h-1.5 rounded-full"
                    style={{
                      background:
                        PAYLINE_COLORS[previewLine % PAYLINE_COLORS.length],
                    }}
                  />
                </div>
              </div>
              <div className="slots-info-line-grid">
                {PAYLINES.map((pattern, i) => (
                  <button
                    key={i}
                    type="button"
                    className={previewLine === i ? "is-on" : ""}
                    onClick={() => setPreviewLine(i)}
                    aria-label={`Payline ${i + 1}`}
                  >
                    <span className="n">{i + 1}</span>
                    <MiniPaylinePreview
                      pattern={pattern}
                      color={
                        PAYLINE_COLORS[i % PAYLINE_COLORS.length] ?? "#fbbf24"
                      }
                      active={previewLine === i}
                    />
                  </button>
                ))}
              </div>
              <p className="slots-info-foot">
                After a win, lines <strong>cycle and blink</strong> one at a
                time over the glass (standard video-slot presentation).
              </p>
            </div>
          )}

          {tab === "math" && (
            <div className="slots-info-math">
              <p className="slots-info-lead">
                Theoretical long-run model for Ethereal Treasure. Short sessions
                vary with volatility — not a certified lab audit certificate.
              </p>
              <table className="slots-info-table">
                <tbody>
                  <tr>
                    <th>Target RTP</th>
                    <td>
                      <strong>{(math.targetRtp * 100).toFixed(1)}%</strong>
                    </td>
                  </tr>
                  <tr>
                    <th>House edge</th>
                    <td>{(math.houseEdge * 100).toFixed(1)}%</td>
                  </tr>
                  <tr>
                    <th>Published band</th>
                    <td>
                      {(math.rtpBand.min * 100).toFixed(0)}–
                      {(math.rtpBand.max * 100).toFixed(0)}%
                    </td>
                  </tr>
                  <tr>
                    <th>Volatility</th>
                    <td>{math.volatility}</td>
                  </tr>
                  <tr>
                    <th>Grid</th>
                    <td>
                      {math.reels}×{math.rows} · {math.lines} lines
                    </td>
                  </tr>
                  <tr>
                    <th>Evaluation</th>
                    <td>Left-to-right · min {SLOT_MATH.minMatch}-of-a-kind</td>
                  </tr>
                  <tr>
                    <th>Match mults</th>
                    <td>
                      3× = {SLOT_MATH.matchMults[0]} · 4× ={" "}
                      {SLOT_MATH.matchMults[1]} · 5× = {SLOT_MATH.matchMults[2]}{" "}
                      (× symbol value × bet/line × wild)
                    </td>
                  </tr>
                  <tr>
                    <th>Max win / paid spin</th>
                    <td>{SLOT_MATH.maxWinMultiple}× total bet (hard cap)</td>
                  </tr>
                  <tr>
                    <th>Big / mega FX</th>
                    <td>
                      ≥{SLOT_MATH.bigWinMultiple}× / ≥
                      {SLOT_MATH.megaWinMultiple}× total bet
                    </td>
                  </tr>
                  <tr>
                    <th>Special spin</th>
                    <td>
                      ~{(SLOT_MATH.specialSpinChance * 100).toFixed(1)}% of paid
                      base spins
                    </td>
                  </tr>
                  <tr>
                    <th>Free spins</th>
                    <td>3/4/5 Malachar → 8/15/20</td>
                  </tr>
                  <tr>
                    <th>Bonus vault</th>
                    <td>3/4/5 seals → 5/7/10 spins</td>
                  </tr>
                  <tr>
                    <th>Most Wanted</th>
                    <td>
                      Durin 12× · Sylara 40× · Kael 100× · Racalvin 400× total
                      bet (3+ posters)
                    </td>
                  </tr>
                  <tr>
                    <th>RNG</th>
                    <td>
                      Web Crypto on Worker · same engine as client preview ·
                      play GBUX debit only
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="slots-info-foot">
                Full SSOT:{" "}
                <code className="text-amber-200/80">docs/SLOTS_MATH_SSOT.md</code>{" "}
                · house edge via symbol weights + feature rates, not client
                faking.
              </p>
            </div>
          )}

          {tab === "privacy" && (
            <div className="slots-info-legal">
              <p className="slots-info-lead">
                How <strong>{BRAND.name}</strong> handles data for slots and the
                wider Service. Full policy:{" "}
                <a href="/privacy" target="_blank" rel="noreferrer">
                  /privacy
                </a>
                .
              </p>
              <h3>What we process for spins</h3>
              <ul>
                <li>
                  <strong>Wallet address</strong> you connect (public key only —
                  never private keys or seeds).
                </li>
                <li>
                  <strong>Play GBUX ledger</strong> — bets, wins, free/bonus
                  state, idempotency keys for spins.
                </li>
                <li>
                  <strong>Technical logs</strong> — Worker request metadata for
                  abuse prevention and integrity (no hole cards; slots outcomes
                  server-dealt).
                </li>
                <li>
                  Optional <strong>Grudge ID</strong> session if you sign in
                  fleet-wide (see fleet privacy on id.grudge-studio.com).
                </li>
              </ul>
              <h3>What we do not collect</h3>
              <ul>
                <li>Seed phrases, private keys, or wallet passwords.</li>
                <li>Microphone / camera for the slots cabinet (not used here).</li>
                <li>
                  Selling personal spin history to third-party advertisers.
                </li>
              </ul>
              <h3>Third parties</h3>
              <ul>
                <li>
                  Wallet providers (e.g. Phantom) under their own policies when
                  you connect.
                </li>
                <li>
                  Cloudflare edge hosting for poker.grudge-studio.com.
                </li>
              </ul>
              <p className="slots-info-foot">
                Contact:{" "}
                <a href="mailto:privacy@grudge-studio.com">
                  privacy@grudge-studio.com
                </a>{" "}
                · Updated with Service policies (August 2026).
              </p>
            </div>
          )}

          {tab === "terms" && (
            <div className="slots-info-legal">
              <p className="slots-info-lead">
                Key terms for playing Ethereal Treasure. Full Terms of Service:{" "}
                <a href="/tos" target="_blank" rel="noreferrer">
                  /tos
                </a>{" "}
                ·{" "}
                <a href="/terms" target="_blank" rel="noreferrer">
                  /terms
                </a>
                .
              </p>
              <h3>Eligibility</h3>
              <ul>
                <li>
                  You must be at least <strong>18</strong> (or age of majority
                  where higher).
                </li>
                <li>
                  You are responsible for local gaming / crypto laws. The
                  Service may be unavailable where prohibited.
                </li>
              </ul>
              <h3>Play GBUX &amp; nature of play</h3>
              <ul>
                <li>
                  Spins debit <strong>play GBUX</strong> (edge ledger), not
                  automatically your on-chain wallet.
                </li>
                <li>
                  Outcomes are determined server-side (Web Crypto). Client
                  animation is presentation only.
                </li>
                <li>
                  RTP and max-win figures are theoretical product disclosures —
                  not guarantees of short-session results.
                </li>
                <li>
                  Cash-out / off-app transfer of play value follows Account Help
                  and staff process — not a silent on-chain auto-withdraw from
                  the cabinet.
                </li>
              </ul>
              <h3>Fairness &amp; abuse</h3>
              <ul>
                <li>
                  No reverse-engineering, bots, or multi-account abuse of the
                  spin API.
                </li>
                <li>
                  We may void / reverse credits that fail integrity checks
                  (invalid session, failed debit, replayed idempotency).
                </li>
              </ul>
              <h3>No professional advice</h3>
              <p>
                Slots are entertainment. Nothing here is investment, legal, or
                gambling advice. Play only with funds you can afford to lose.
              </p>
              <p className="slots-info-foot">
                Operator: Grudge Studio ·{" "}
                <a href="mailto:games@grudgestudio.org">
                  games@grudgestudio.org
                </a>
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function SpecialSpinOverlay({ onDismiss }: { onDismiss: () => void }) {
  return (
    <FxSplash
      bg={BRAND.slots.fxBonus}
      title="RACALVIN SPECIAL"
      sub="NEXT SPIN · JACKPOT REELS"
      badge="CLOUD PILOT"
      onDismiss={onDismiss}
      autoMs={3400}
      coins={false}
    />
  );
}

/** Jackpot hit → graphical novel page for that bounty (SFX inside novel) */
function JackpotWinOverlay({
  awards,
  total,
  onDismiss,
}: {
  awards: JackpotAward[];
  total: number;
  onDismiss: () => void;
}) {
  return (
    <WantedGraphicalNovel
      open
      mode="jackpot"
      awards={awards}
      winTotal={total}
      startAt={awards[0]?.tier}
      onClose={onDismiss}
    />
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function SlotsPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { wallet, refreshBalances, openWalletPanel } = useWalletState();
  const { ensurePlaySession } = useDappSign();
  const walletAddress = wallet.publicKey ?? "";
  const { mode: displayMode, isFs, toggleFullscreen } = useSlotsDisplayMode();
  const reelWindowRef = useRef<HTMLDivElement>(null);

  const [reelGrid, setReelGrid] = useState<string[][]>(makeDefaultGrid);
  const [wildCells, setWildCells] = useState<WildCell[]>([]);
  const [winRows, setWinRows] = useState<Map<number, number[]>>(new Map());
  const [allWinPositions, setAllWinPositions] = useState<
    PaylineResult[] | null
  >(null);
  const [activePaylineIdx, setActivePaylineIdx] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [spinToken, setSpinToken] = useState(0);
  const [lastResult, setLastResult] = useState<SpinResult | null>(null);
  const [playBalance, setPlayBalance] = useState<number | null>(null);
  const [fundOpen, setFundOpen] = useState(false);
  const [showWantedNovel, setShowWantedNovel] = useState(false);

  const [freeSpinMode, setFreeSpinMode] = useState(false);
  const [freeSpinsRemaining, setFreeSpinsRemaining] = useState(0);
  const [freeSpinsMultiplier, setFreeSpinsMultiplier] = useState(1);
  const [showFreeSpinTrigger, setShowFreeSpinTrigger] = useState(false);
  const [pendingFreeSpinData, setPendingFreeSpinData] = useState<{
    spins: number;
    mult: number;
  } | null>(null);

  const [bonusMode, setBonusMode] = useState(false);
  const [bonusSpinsRemaining, setBonusSpinsRemaining] = useState(0);
  const [bonusSpinsMultiplier, setBonusSpinsMultiplier] = useState(1);
  const [showBonusTrigger, setShowBonusTrigger] = useState(false);
  const [pendingBonusData, setPendingBonusData] = useState<{
    spins: number;
    mult: number;
  } | null>(null);
  const [specialPending, setSpecialPending] = useState(false);
  const [showSpecialTrigger, setShowSpecialTrigger] = useState(false);
  const [showJackpotWin, setShowJackpotWin] = useState(false);
  const [jackpotAwardsUi, setJackpotAwardsUi] = useState<JackpotAward[]>([]);
  const [jackpotWinUi, setJackpotWinUi] = useState(0);

  const [showBigWin, setShowBigWin] = useState(false);
  const [bigWinAmount, setBigWinAmount] = useState(0);
  const [isJackpot, setIsJackpot] = useState(false);

  const [betPerLine, setBetPerLine] = useState(0.5);
  const [lines, setLines] = useState(MAX_LINES);
  const [autoSpin, setAutoSpin] = useState(false);
  const [showPayTable, setShowPayTable] = useState(false);
  const [leftRailOpen, setLeftRailOpen] = useState(() => getLeftRailOpenPref());
  const [wantedHits, setWantedHits] = useState<WantedHitStats>(emptyWantedHits);
  /** Per-tier collect count this session (every Wanted icon that lands) */
  const [wantedCollect, setWantedCollect] =
    useState<WantedHitStats>(emptyWantedHits);
  const [wantedPulse, setWantedPulse] = useState<Partial<Record<JackpotTierId, boolean>>>(
    {},
  );
  const [wantedFlyers, setWantedFlyers] = useState<WantedFlyer[]>([]);
  /** Guard framer-motion multi-fire of onAnimationComplete */
  const wantedFlyerDoneIds = useRef(new Set<string>());
  const machineRef = useRef<HTMLDivElement>(null);
  const jpBadgeRefs = useRef<Partial<Record<JackpotTierId, HTMLDivElement | null>>>(
    {},
  );
  const spinTimersRef = useRef<number[]>([]);
  const [stopDelays, setStopDelays] = useState<number[]>(() => [
    ...BASE_STOP_DELAYS,
  ]);
  const [anticipationPlan, setAnticipationPlan] =
    useState<AnticipationPlan | null>(null);
  const [anticipationLive, setAnticipationLive] = useState(false);

  const clearSpinTimers = useCallback(() => {
    for (const id of spinTimersRef.current) window.clearTimeout(id);
    spinTimersRef.current = [];
  }, []);

  const [sessionWon, setSessionWon] = useState(0);
  const [sessionWager, setSessionWager] = useState(0);
  const [spinCount, setSpinCount] = useState(0);
  /** Machine motion classes: land / win / bigwin */
  const [machineFx, setMachineFx] = useState<"" | "fx-land" | "fx-win" | "fx-bigwin">("");
  const [showSparks, setShowSparks] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [leverPulling, setLeverPulling] = useState(false);
  const [leverImgOk, setLeverImgOk] = useState(true);
  /** Circle buttons open bet/lines slider popovers */
  const [openSlider, setOpenSlider] = useState<null | "bet" | "lines">(null);
  const [showIntro, setShowIntro] = useState(() => {
    try {
      return sessionStorage.getItem("racalvin-intro-seen") !== "1";
    } catch {
      return true;
    }
  });
  const dismissIntro = useCallback(() => {
    setShowIntro(false);
    try {
      sessionStorage.setItem("racalvin-intro-seen", "1");
    } catch {
      /* ignore */
    }
    slotsAudio.startBgm();
  }, []);

  // BGM + audio unlock while on /slots (stops on leave)
  useEffect(() => {
    if (!showIntro) {
      slotsAudio.unlock();
      slotsAudio.startBgm();
    }
    // First tap anywhere on page unlocks WebAudio path (mobile browsers)
    const unlock = () => {
      slotsAudio.unlock();
      if (!showIntro) slotsAudio.startBgm();
    };
    window.addEventListener("pointerdown", unlock, { once: true, passive: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      slotsAudio.stopBgm();
    };
  }, [showIntro]);

  // Fullscreen = sea BGM + one Racalvin welcome; leave = cabinet bandoneon
  const fsWelcomed = useRef(false);
  useEffect(() => {
    if (showIntro) return;
    if (isFs) {
      slotsAudio.unlock();
      slotsAudio.setBgmMode("sea");
      slotsAudio.startBgm();
      if (!fsWelcomed.current) {
        fsWelcomed.current = true;
        slotsAudio.welcome();
      } else {
        slotsAudio.pirate("pirata", 0.2);
      }
    } else {
      slotsAudio.setBgmMode("cabinet");
      slotsAudio.startBgm();
    }
  }, [isFs, showIntro]);

  // Arcade sprite font pack (Classic Sevens yellow) for marquee / wins
  useEffect(() => {
    preloadSevensFont("yellow");
    preloadSevensFont("red");
  }, []);

  // Admin / published plate layout (CSS vars on machine)
  const [layoutCss, setLayoutCss] = useState<Record<string, string>>(() =>
    slotsLayoutStyle(resolveSlotsLayout()),
  );
  useEffect(() => {
    void fetchPublishedLayouts().then(() => {
      setLayoutCss(slotsLayoutStyle(resolveSlotsLayout()));
    });
    const onStorage = (e: StorageEvent) => {
      if (
        e.key === "poker_admin_layout_draft_v1" ||
        e.key === "poker_layout_published_v1"
      ) {
        setLayoutCss(slotsLayoutStyle(resolveSlotsLayout()));
      }
    };
    window.addEventListener("storage", onStorage);
    const t = window.setInterval(() => {
      setLayoutCss(slotsLayoutStyle(resolveSlotsLayout()));
    }, 4000);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearInterval(t);
    };
  }, []);

  // Immersive shell: hide fleet bottom nav in FS / landscape cabinet focus
  useEffect(() => {
    document.body.classList.toggle("slots-immersive", isFs);
    document.body.dataset.slotsMode = displayMode;
    return () => {
      document.body.classList.remove("slots-immersive");
      delete document.body.dataset.slotsMode;
    };
  }, [isFs, displayMode]);

  const totalBet = Math.round(betPerLine * lines * 100) / 100;

  /**
   * Wanted posters fly the instant a reel lands (per-reel, not after all stop).
   */
  const launchWantedFlyoutsForReel = useCallback(
    (grid: string[][], reel: number) => {
      const winEl = reelWindowRef.current;
      if (!winEl || !grid?.length) return;
      const reels = winEl.querySelectorAll<HTMLElement>(".slots-reel");
      const reelEl = reels[reel];
      const col = grid[reel];
      if (!reelEl || !col) return;

      const rr = reelEl.getBoundingClientRect();
      const cellH = rr.height / ROW_COUNT;
      const cells = reelEl.querySelectorAll<HTMLElement>(".slots-cell");
      const cellBase = Math.max(0, cells.length - ROW_COUNT);
      const flyers: WantedFlyer[] = [];
      let n = 0;
      for (let row = 0; row < ROW_COUNT; row++) {
        const sym = col[row];
        if (!sym || !JP_SYMBOL_IDS.has(sym)) continue;
        const tier = sym as JackpotTierId;
        const lore = JACKPOT_TIERS[tier];
        if (!lore) continue;
        const badge = jpBadgeRefs.current[tier];
        const br = badge?.getBoundingClientRect();
        const cellEl = cells[cellBase + row];
        const cr = cellEl?.getBoundingClientRect();
        const fromX = cr ? cr.left + cr.width / 2 : rr.left + rr.width / 2;
        const fromY = cr
          ? cr.top + cr.height / 2
          : rr.top + cellH * row + cellH / 2;
        const toX = br ? br.left + br.width / 2 : fromX;
        const toY = br ? br.top + br.height / 2 : Math.max(48, fromY - 120);
        flyers.push({
          id: `wf-${Date.now()}-${reel}-${row}-${tier}-${n}`,
          tier,
          poster: lore.poster || lore.icon,
          fromX,
          fromY,
          toX,
          toY,
          delay: n * 0.05,
        });
        n += 1;
      }
      if (!flyers.length) return;
      setWantedFlyers((prev) => [...prev, ...flyers]);
    },
    [],
  );

  const onWantedFlyerDone = useCallback((flyer: WantedFlyer) => {
    if (wantedFlyerDoneIds.current.has(flyer.id)) return;
    wantedFlyerDoneIds.current.add(flyer.id);
    setWantedCollect((prev) => ({
      ...prev,
      [flyer.tier]: (prev[flyer.tier] || 0) + 1,
    }));
    setWantedPulse((p) => ({ ...p, [flyer.tier]: true }));
    window.setTimeout(() => {
      setWantedPulse((p) => ({ ...p, [flyer.tier]: false }));
    }, 520);
    setWantedFlyers((list) => list.filter((f) => f.id !== flyer.id));
  }, []);

  const spinMutation = useMutation({
    mutationFn: async () => {
      const idempotencyKey =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `spin-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      // Session sign once in mid-app UI — not every spin
      await ensurePlaySession();
      const headers = playSessionHeaders(walletAddress, {
        "Content-Type": "application/json",
        "Idempotency-Key": idempotencyKey,
      });
      const res = await fetch("/api/slots/spin", {
        method: "POST",
        headers,
        body: JSON.stringify({
          walletAddress,
          betPerLine,
          lines,
          idempotencyKey,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Spin failed");
      return data as { result: SpinResult; gbuxBalance: number };
    },
    onMutate: () => {
      // Lock UI + audio immediately; visual reels start when result plans stops
      clearSpinTimers();
      setSpinning(true);
      setWinRows(new Map());
      setAllWinPositions(null);
      setActivePaylineIdx(0);
      setLastResult(null);
      setWantedFlyers([]);
      wantedFlyerDoneIds.current.clear();
      setAnticipationPlan(null);
      setAnticipationLive(false);
      slotsAudio.unlock();
      slotsAudio.spinStart();
    },
    onSuccess: ({ result, gbuxBalance }) => {
      if (typeof gbuxBalance === "number") {
        setPlayBalance(gbuxBalance);
        void refreshBalances();
      }

      const plan = planReelStops(result.reelGrid, result);
      const delays = plan.stopDelays;
      const maxStop = delays[REEL_COUNT - 1]! + REEL_LAND_SETTLE_MS;

      setStopDelays(delays);
      setAnticipationPlan(plan.level > 0 ? plan : null);
      setReelGrid(result.reelGrid);
      setWildCells(result.wildCells);
      // Kick strip animation with known grid + anticipation timings
      setSpinToken((t) => t + 1);

      // Anticipation banner when earlier reels have landed and a hot reel still spins
      if (plan.level > 0) {
        const banId = window.setTimeout(() => {
          setAnticipationLive(true);
          try {
            slotsAudio.reelTick();
          } catch {
            /* optional */
          }
        }, plan.bannerAt);
        spinTimersRef.current.push(banId);
      }

      // Settle: stop SFX, pays, overlays (Wanted fly-outs fire per-reel onLand)
      const settleId = window.setTimeout(() => {
        setAnticipationLive(false);
        setAnticipationPlan(null);
        slotsAudio.spinStop();
        setLastResult(result);
        if (result.totalWin > 0) {
          if (result.isJackpot) slotsAudio.win("jackpot");
          else if (result.isBigWin) slotsAudio.win("big");
          else if (result.freeSpinsTriggered) slotsAudio.win("free");
          else if (result.bonusTriggered) slotsAudio.win("bonus");
          else slotsAudio.win("normal");
        } else {
          slotsAudio.lose();
        }
        const pls = result.paylineResults ?? [];
        setAllWinPositions(pls.length ? pls : null);
        setActivePaylineIdx(0);
        const newWinRows = new Map<number, number[]>();
        const focus = pls[0];
        for (const pos of focus?.positions ?? []) {
          const cur = newWinRows.get(pos.reel) ?? [];
          if (!cur.includes(pos.row)) cur.push(pos.row);
          newWinRows.set(pos.reel, cur);
        }
        for (const pos of [
          ...(result.scatterPositions ?? []),
          ...(result.bonusPositions ?? []),
        ]) {
          const cur = newWinRows.get(pos.reel) ?? [];
          if (!cur.includes(pos.row)) cur.push(pos.row);
          newWinRows.set(pos.reel, cur);
        }
        setWinRows(newWinRows);

        setMachineFx("fx-land");
        if (result.totalWin > 0) {
          setShowSparks(true);
          setShowFlash(true);
          const big =
            result.isJackpot ||
            result.isBigWin ||
            result.totalWin >= result.totalBet * 10;
          window.setTimeout(() => {
            setMachineFx(big ? "fx-bigwin" : "fx-win");
          }, 100);
          window.setTimeout(() => setShowSparks(false), big ? 1600 : 1100);
          window.setTimeout(() => setShowFlash(false), big ? 1000 : 700);
          window.setTimeout(() => setMachineFx(""), big ? 2000 : 1400);
        } else {
          window.setTimeout(() => setMachineFx(""), 450);
        }

        setSessionWon((p) => p + result.totalWin);
        if (result.jackpotAwards?.length) {
          setWantedHits((prev) => {
            const next = { ...prev };
            for (const a of result.jackpotAwards!) {
              if (a.tier in next) {
                next[a.tier as JackpotTierId] =
                  (next[a.tier as JackpotTierId] || 0) + 1;
              }
            }
            return next;
          });
        }
        setSessionWager((p) => p + result.totalBet);
        setSpinCount((p) => p + 1);
        setFreeSpinsRemaining(result.freeSpinsRemaining);
        setFreeSpinsMultiplier(result.freeSpinsMultiplier);
        setBonusSpinsRemaining(result.bonusSpinsRemaining ?? 0);
        setBonusSpinsMultiplier(result.bonusSpinsMultiplier ?? 1);
        setSpecialPending((result.specialSpinsRemaining ?? 0) > 0);

        if (result.freeSpinsTriggered && !result.isFreeSpinMode) {
          setPendingFreeSpinData({
            spins: result.freeSpinsAwarded,
            mult: result.freeSpinsMultiplier,
          });
          setShowFreeSpinTrigger(true);
          setFreeSpinMode(true);
        }
        if (result.isFreeSpinMode && result.freeSpinsRemaining === 0) {
          setFreeSpinMode(false);
        }

        if (result.bonusTriggered && !result.isBonusMode) {
          setPendingBonusData({
            spins: result.bonusSpinsAwarded ?? 0,
            mult: result.bonusSpinsMultiplier ?? 2,
          });
          setShowBonusTrigger(true);
          setBonusMode(true);
        }
        if (result.isBonusMode && (result.bonusSpinsRemaining ?? 0) === 0) {
          setBonusMode(false);
          toast({
            title: "Bonus Spins Complete",
            description: `Last bonus win ${result.totalWin} GBUX`,
          });
        }

        if (result.specialSpinTriggered) {
          setShowSpecialTrigger(true);
          setSpecialPending(true);
        }

        const awards = result.jackpotAwards ?? [];
        if (awards.length > 0 && (result.jackpotWin ?? 0) > 0) {
          setJackpotAwardsUi(awards);
          setJackpotWinUi(result.jackpotWin ?? 0);
          setShowJackpotWin(true);
        } else if (result.isJackpot || result.isBigWin) {
          setBigWinAmount(result.totalWin);
          setIsJackpot(result.isJackpot);
          setShowBigWin(true);
        }
        setSpinning(false);
      }, maxStop);
      spinTimersRef.current.push(settleId);
    },
    onError: (err: Error) => {
      clearSpinTimers();
      setSpinning(false);
      setAnticipationPlan(null);
      setAnticipationLive(false);
      toast({ title: "Spin failed", description: err.message, variant: "destructive" });
    },
  });

  const overlayBusy =
    showBigWin ||
    showFreeSpinTrigger ||
    showBonusTrigger ||
    showSpecialTrigger ||
    showJackpotWin;

  const autoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerSpin = useCallback(() => {
    if (spinning || overlayBusy || leverPulling) return;
    if (!walletAddress || walletAddress.length < 32) {
      toast({
        title: "Connect wallet",
        description: "Open wallet panel to connect and fund play GBUX.",
        variant: "destructive",
      });
      openWalletPanel("connect");
      return;
    }
    const play = playBalance ?? wallet.playGbux ?? 0;
    const inFeature = bonusMode || freeSpinMode || specialPending;
    if (play < totalBet && !inFeature) {
      setFundOpen(true);
      return;
    }
    spinMutation.mutate();
  }, [
    spinning,
    overlayBusy,
    leverPulling,
    walletAddress,
    spinMutation,
    toast,
    openWalletPanel,
    playBalance,
    wallet.playGbux,
    totalBet,
    bonusMode,
    freeSpinMode,
    specialPending,
  ]);

  /** Red handle: LMB pull animation then spin */
  const pullLever = useCallback(() => {
    if (spinning || overlayBusy || leverPulling) return;
    if (!walletAddress || walletAddress.length < 32) {
      toast({
        title: "Connect wallet",
        description: "Pull handle after connecting play wallet.",
        variant: "destructive",
      });
      openWalletPanel("connect");
      return;
    }
    setLeverPulling(true);
    slotsAudio.lever();
    window.setTimeout(() => {
      spinMutation.mutate();
    }, 280);
    window.setTimeout(() => setLeverPulling(false), 600);
  }, [
    spinning,
    overlayBusy,
    leverPulling,
    walletAddress,
    spinMutation,
    toast,
    openWalletPanel,
  ]);

  useEffect(() => {
    if (autoSpin && !spinning && !overlayBusy) {
      autoRef.current = setTimeout(() => triggerSpin(), 700);
    }
    return () => {
      if (autoRef.current) clearTimeout(autoRef.current);
    };
  }, [autoSpin, spinning, overlayBusy, triggerSpin]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Space" && !e.repeat) {
        e.preventDefault();
        triggerSpin();
      }
      if (e.code === "KeyF" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        void toggleFullscreen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [triggerSpin, toggleFullscreen]);

  /** Cycle winning paylines + blink matching cells (industry presentation). */
  useEffect(() => {
    if (spinning || !allWinPositions?.length || !lastResult) return;
    if (allWinPositions.length <= 1) return;
    const id = window.setInterval(() => {
      setActivePaylineIdx((i) => {
        const next = (i + 1) % allWinPositions.length;
        const map = new Map<number, number[]>();
        for (const pos of allWinPositions[next]?.positions ?? []) {
          const cur = map.get(pos.reel) ?? [];
          if (!cur.includes(pos.row)) cur.push(pos.row);
          map.set(pos.reel, cur);
        }
        for (const pos of [
          ...(lastResult.scatterPositions ?? []),
          ...(lastResult.bonusPositions ?? []),
        ]) {
          const cur = map.get(pos.reel) ?? [];
          if (!cur.includes(pos.row)) cur.push(pos.row);
          map.set(pos.reel, cur);
        }
        setWinRows(map);
        return next;
      });
    }, 950);
    return () => window.clearInterval(id);
  }, [spinning, allWinPositions, lastResult]);

  const rtp =
    sessionWager > 0 ? Math.round((sessionWon / sessionWager) * 1000) / 10 : 0;

  const featureMode = bonusMode || freeSpinMode || specialPending;
  const marqueeText = bonusMode
    ? `ETHEREAL VAULT · ${bonusSpinsRemaining} LEFT · ${bonusSpinsMultiplier}×`
    : freeSpinMode
      ? `VOYAGE FREE · ${freeSpinsRemaining} LEFT · ${freeSpinsMultiplier}×`
      : specialPending
        ? "RACALVIN SPECIAL READY"
        : lastResult && !spinning && lastResult.totalWin > 0
          ? `WIN ${lastResult.totalWin} GBUX`
          : `${BRAND.slots.title}`;

  const activeWin = allWinPositions?.[activePaylineIdx % (allWinPositions?.length || 1)];

  return (
    <div
      className={`slots-page slots-page--sevens slots-page--${displayMode} ${
        isFs ? "slots-page--fullscreen slots-fs-container" : ""
      } ${spinning ? "spinning" : ""} ${
        freeSpinMode || bonusMode ? "free-mode" : ""
      } ${bonusMode ? "bonus-mode" : ""}`}
    >
      <FundPlayDialog
        open={fundOpen}
        onClose={() => setFundOpen(false)}
        playGbux={playBalance ?? wallet.playGbux ?? 0}
        game="slots"
        onFund={() => openWalletPanel("deposit")}
      />
      <AnimatePresence>
        {showIntro && <IntroVideoOverlay onDone={dismissIntro} />}
      </AnimatePresence>
      <div
        className="slots-ambient"
        style={{ backgroundImage: `url(${BRAND.slots.ambient})` }}
        aria-hidden
      />
      {/* Fullscreen sea video bed — progressive conquest loop */}
      {isFs ? (
        <video
          className="slots-fs-video"
          src={BRAND.slots.progressiveVideo}
          autoPlay
          muted
          loop
          playsInline
          poster={BRAND.slots.ambient}
          aria-hidden
        />
      ) : null}
      <AmbientDust count={isFs ? 28 : 18} />

      <SlotsLeftRail
        open={leftRailOpen}
        onOpenChange={setLeftRailOpen}
        wantedHits={wantedHits}
        sessionWon={sessionWon}
        sessionWager={sessionWager}
        totalBet={totalBet}
        lastWin={lastResult?.totalWin ?? 0}
        onOpenNovel={() => {
          setLeftRailOpen(false);
          setShowWantedNovel(true);
        }}
        onOpenLegal={() => {
          setLeftRailOpen(false);
          setShowPayTable(true);
        }}
      />

      <div className={`slots-shell ${leftRailOpen ? "slots-shell--rail-open" : ""}`}>
        <header className="slots-header">
          <button
            onClick={() => navigate("/gbuxgames")}
            className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm"
          >
            <ChevronLeft size={18} /> Games
          </button>
          <div className="flex flex-col items-center min-w-0">
            <div className="slots-title text-amber-300 truncate max-w-[55vw]">
              {bonusMode
                ? "ETHEREAL VAULT"
                : freeSpinMode
                  ? "SEA VOYAGE"
                  : BRAND.slots.title}
            </div>
            <div className="text-[10px] text-white/45 tracking-widest uppercase truncate flex items-center gap-1.5">
              {displayMode === "phone" ? (
                <Smartphone size={10} className="opacity-70" />
              ) : null}
              {BRAND.slots.subtitle}
              {activeWin && !spinning ? (
                <span className="text-amber-300/90 normal-case tracking-normal">
                  · L{activeWin.paylineIndex + 1} +{activeWin.win}
                </span>
              ) : null}
            </div>
            <PlaySessionStatusLine className="text-[10px] text-emerald-400/80 normal-case tracking-normal" />
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => {
                slotsAudio.unlock();
                void toggleFullscreen();
              }}
              className="slots-fs-btn text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10"
              aria-label={isFs ? "Exit fullscreen" : "Fullscreen"}
              title={isFs ? "Exit fullscreen" : "Fullscreen play — giant 5×5"}
            >
              {isFs ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button
              type="button"
              onClick={() => {
                slotsAudio.unlock();
                setLeftRailOpen(true);
              }}
              className="text-white/60 hover:text-white p-1.5"
              aria-label="Wanted progressive and paytable"
              title="Info · Wanted · combos"
            >
              <Info size={20} />
            </button>
          </div>
        </header>

        {isFs ? (
          <div className="slots-fs-progress">
            <ProgressiveSeaBanner
              sessionWon={sessionWon}
              sessionWager={sessionWager}
            />
          </div>
        ) : null}

        {/* ONE canvas: cabinet art + every UI plate / control / lever */}
        <div className={`slots-stage ${isFs ? "slots-stage--fs" : ""}`}>
          <div
            ref={machineRef}
            className={`slots-machine ${machineFx} ${spinning ? "is-spinning" : ""} ${
              isFs ? "slots-machine--fs" : ""
            }`}
            /* Fullscreen owns plate % via CSS — don't let published layout squash giant 5×5 */
            style={isFs ? undefined : (layoutCss as CSSProperties)}
          >
            <img
              className="slots-cabinet-img"
              src={BRAND.slots.cabinet}
              alt="Racalvin Ethereal Treasure machine"
              draggable={false}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                if (!img.src.includes("cabinet-classic")) {
                  img.src = "/slots/craftpix/cabinet-classic-sevens.jpg";
                }
              }}
            />
            <div
              className={`slots-flash ${showFlash ? "active" : ""} ${
                showFlash && machineFx === "fx-bigwin" ? "strobe" : ""
              }`}
              aria-hidden
            />

            {/*
              Wanted crown strip — counters L/R of skull crown art.
              Durin · Sylara | skull/crown | Kael · Racalvin
            */}
            <button
              type="button"
              className="slots-jp-on-canvas"
              onClick={() => {
                slotsAudio.unlock();
                setShowWantedNovel(true);
              }}
              aria-label="Open Grudge Most Wanted graphical novel"
              title="Most Wanted · collect posters on reels"
            >
              {JACKPOT_ORDER.map((tier) => (
                <JackpotTierBadge
                  key={tier}
                  tier={tier}
                  compact
                  count={wantedCollect[tier] || 0}
                  pulse={Boolean(wantedPulse[tier])}
                  badgeRef={(el) => {
                    jpBadgeRefs.current[tier] = el;
                  }}
                />
              ))}
            </button>

            {/* Feature ribbon under crown */}
            {bonusMode && !showBonusTrigger && (
              <div className="slots-feature-ribbon bonus">
                <span>ETHEREAL VAULT</span>
                <span>
                  {bonusSpinsRemaining} · {bonusSpinsMultiplier}×
                </span>
              </div>
            )}
            {freeSpinMode && !showFreeSpinTrigger && !bonusMode && (
              <div className="slots-feature-ribbon free">
                <span>SEA VOYAGE</span>
                <span>
                  {freeSpinsRemaining} · {freeSpinsMultiplier}×
                </span>
              </div>
            )}
            {specialPending && !bonusMode && !freeSpinMode && (
              <div className="slots-feature-ribbon special">
                <span>RACALVIN SPECIAL</span>
                <span>NEXT SPIN</span>
              </div>
            )}

            {/* Marquee plate — sprite font when short; text fallback for long strings */}
            <div className="slots-marquee">
              <div
                className={`slots-marquee-label relative ${
                  !spinning && lastResult && lastResult.totalWin > 0 ? "is-win" : ""
                }`}
              >
                {marqueeText.length <= 18 ? (
                  <SpriteFont
                    text={marqueeText}
                    color={
                      !spinning && lastResult && lastResult.totalWin > 0
                        ? "yellow"
                        : bonusMode
                          ? "red"
                          : freeSpinMode
                            ? "blue"
                            : "yellow"
                    }
                    height={22}
                    maxWidth={280}
                    tracking={0.03}
                  />
                ) : (
                  marqueeText
                )}
              </div>
            </div>

            {/* Main glass — reels + measured payline overlay */}
            <div
              ref={reelWindowRef}
              className={`slots-reel-window slots-reel-window--5x5 ${
                freeSpinMode || bonusMode ? "free-mode" : ""
              } ${bonusMode ? "bonus-mode" : ""} ${
                !spinning && (allWinPositions?.length ?? 0) > 0
                  ? "has-win-lines"
                  : ""
              } ${
                anticipationLive && anticipationPlan
                  ? `is-anticipation lvl-${anticipationPlan.level} kind-${anticipationPlan.kind ?? "wanted"}`
                  : ""
              }`}
            >
              {anticipationLive && anticipationPlan?.label ? (
                <div
                  className={`slots-anticipate-banner lvl-${anticipationPlan.level} kind-${anticipationPlan.kind ?? "wanted"}`}
                  aria-live="polite"
                >
                  {anticipationPlan.label}
                </div>
              ) : null}
              {Array.from({ length: REEL_COUNT }, (_, reel) => {
                const inHotPlan = Boolean(
                  anticipationPlan?.hotReels.includes(reel),
                );
                const spinActive = spinning && spinToken > 0;
                // Timing (long ease) from plan at spin start; glow only in tease window
                const isHotTiming = inHotPlan && spinActive;
                const isHotGlow =
                  inHotPlan && spinActive && anticipationLive;
                return (
                  <AnimatedReel
                    key={reel}
                    symbols={
                      reelGrid[reel] ??
                      Array.from(
                        { length: ROW_COUNT },
                        () => "dice" as SymbolId,
                      )
                    }
                    wildRows={wildCells.filter((w) => w.reel === reel)}
                    winRows={winRows.get(reel) ?? []}
                    spinning={spinActive}
                    stopDelay={stopDelays[reel] ?? BASE_STOP_DELAYS[reel] ?? 700}
                    spinToken={spinToken}
                    isFreeSpins={freeSpinMode || bonusMode}
                    anticipation={isHotTiming}
                    anticipationGlow={isHotGlow}
                    anticipationLevel={
                      (isHotTiming
                        ? anticipationPlan?.level ?? 0
                        : 0) as 0 | 1 | 2 | 3
                    }
                    onLand={() => {
                      // Wanted posters launch the moment this reel lands
                      launchWantedFlyoutsForReel(reelGrid, reel);
                    }}
                  />
                );
              })}
              {!spinning && allWinPositions && allWinPositions.length > 0 && (
                <PaylineOverlay
                  results={allWinPositions}
                  windowRef={reelWindowRef}
                  measureKey={spinToken + (isFs ? 1000 : 0)}
                  activeIndex={activePaylineIdx}
                />
              )}
              <WinSparks active={showSparks || machineFx === "fx-bigwin"} />
              {/* Classic Sevens-style win ring flash over glass */}
              <div
                className={`slots-win-ring ${
                  showSparks || machineFx === "fx-win" || machineFx === "fx-bigwin"
                    ? "is-on"
                    : ""
                }`}
                style={{
                  backgroundImage: `url(${CRAFTPIX_WIN_RING})`,
                }}
                aria-hidden
              />
              {!spinning && allWinPositions && allWinPositions.length > 0 ? (
                <div className="slots-line-hud" aria-live="polite">
                  Line {activePaylineIdx + 1}/{allWinPositions.length}
                  {activeWin ? ` · +${activeWin.win}` : ""}
                </div>
              ) : null}
              {/* Fullscreen floating win amount (cabinet strip is docked) */}
              {isFs && !spinning && lastResult && lastResult.totalWin > 0 ? (
                <div className="slots-fs-win-hud" aria-live="polite">
                  <span className="slots-fs-win-hud__label">WIN</span>
                  <SpriteWinAmount
                    amount={lastResult.totalWin}
                    color="yellow"
                    height={36}
                  />
                  {allWinPositions && allWinPositions.length > 0 ? (
                    <span className="slots-fs-win-hud__lines">
                      {allWinPositions.length} payline
                      {allWinPositions.length > 1 ? "s" : ""}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* Win strip on art black bar */}
            <div
              className={`slots-win-readout ${spinning ? "is-spin" : ""} ${
                !spinning && lastResult && lastResult.totalWin > 0 ? "is-win" : ""
              }`}
            >
              <span className="slots-win-readout__main">
                {spinning || leverPulling ? (
                  <SpriteFont text="SPIN" color="red" height={26} maxWidth={160} />
                ) : lastResult ? (
                  lastResult.totalWin > 0 ? (
                    <SpriteWinAmount
                      amount={lastResult.totalWin}
                      color="yellow"
                      height={28}
                    />
                  ) : (
                    <SpriteFont text="—" color="white" height={22} />
                  )
                ) : playBalance != null ? (
                  <SpriteFont
                    text={`${Math.round(playBalance)}`}
                    color="green"
                    height={24}
                    maxWidth={200}
                  />
                ) : (
                  <SpriteFont text="READY" color="yellow" height={22} />
                )}
              </span>
              <span className="meta">
                RTP {(SLOT_MATH.targetRtp * 100).toFixed(1)}% · bet {totalBet} ·{" "}
                {spinCount} spins
              </span>
            </div>

            {/* Transparent lever */}
            <button
              type="button"
              className={`slots-lever ${leverPulling ? "is-pulling" : ""}`}
              disabled={spinning || overlayBusy || leverPulling}
              onClick={pullLever}
              aria-label="Pull handle to spin"
              title="Pull handle to spin"
            >
              <span className="slots-lever-inner" aria-hidden>
                {leverImgOk ? (
                  <img
                    className="slots-lever-img"
                    src={BRAND.slots.lever}
                    alt=""
                    draggable={false}
                    onError={() => setLeverImgOk(false)}
                  />
                ) : (
                  <span className="slots-lever-css">
                    <span className="slots-lever-shaft" />
                    <span className="slots-lever-ball" />
                    <span className="slots-lever-base" />
                  </span>
                )}
              </span>
              <span className="slots-lever-hit" />
            </button>

            {/* Dock / art spots: BET · LINES · SPIN · AUTO · INFO */}
            <div
              className={`slots-control-dock ${isFs ? "slots-control-dock--fs" : ""}`}
              aria-label="Slot controls"
            >
            <button
              type="button"
              className={`slots-spot slots-spot--circle slots-spot--bet ${
                openSlider === "bet" ? "is-open" : ""
              }`}
              disabled={spinning || featureMode}
              onClick={() => {
                slotsAudio.unlock();
                setOpenSlider((s) => (s === "bet" ? null : "bet"));
              }}
              aria-label="Bet amount"
              title="Bet per line"
            >
              <span className="k">BET</span>
              <span>{betPerLine}</span>
            </button>
            <button
              type="button"
              className={`slots-spot slots-spot--circle slots-spot--lines ${
                openSlider === "lines" ? "is-open" : ""
              }`}
              disabled={spinning || featureMode}
              onClick={() => {
                slotsAudio.unlock();
                setOpenSlider((s) => (s === "lines" ? null : "lines"));
              }}
              aria-label="Paylines"
              title="Paylines"
            >
              <span className="k">LINES</span>
              <span>{lines}</span>
            </button>
            <button
              type="button"
              className={`slots-spot slots-spot--spin has-art ${spinning ? "spinning" : ""} ${
                freeSpinMode || bonusMode || specialPending ? "free" : ""
              }`}
              disabled={spinning || overlayBusy}
              onClick={() => {
                setOpenSlider(null);
                slotsAudio.unlock();
                triggerSpin();
              }}
              aria-label={
                spinning
                  ? "Spinning"
                  : bonusMode
                    ? "Bonus spin"
                    : freeSpinMode
                      ? "Free spin"
                      : specialPending
                        ? "Special spin"
                        : "Spin"
              }
              title="Spin"
              style={
                spinning
                  ? { backgroundImage: `url(${CRAFTPIX_SPIN_PRESS})` }
                  : undefined
              }
            >
              {/* SPIN is stamped on CraftPix Spin1.png — no text overlay.
                  Mode chips only when not base play. */}
              {!spinning && (bonusMode || freeSpinMode || specialPending) ? (
                <span className="slots-spot-spin-mode">
                  {bonusMode ? "BONUS" : freeSpinMode ? "FREE" : "SPECIAL"}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className={`slots-spot slots-spot--rect slots-spot--auto ${
                autoSpin ? "is-on" : ""
              }`}
              disabled={!walletAddress}
              onClick={() => {
                setOpenSlider(null);
                slotsAudio.unlock();
                setAutoSpin((a) => !a);
              }}
              aria-label={autoSpin ? "Stop auto" : "Auto spin"}
            >
              {autoSpin ? "STOP" : "AUTO"}
            </button>
            <button
              type="button"
              className="slots-spot slots-spot--rect slots-spot--info"
              onClick={() => {
                setOpenSlider(null);
                slotsAudio.unlock();
                setLeftRailOpen(true);
              }}
              aria-label="Wanted progressive and paytable"
            >
              INFO
            </button>
            {isFs ? (
              <div className="slots-fs-dock-meta" aria-hidden>
                <span>BET {totalBet}</span>
                <span>{lines}L</span>
                <span>RTP {(SLOT_MATH.targetRtp * 100).toFixed(0)}%</span>
                {sessionWon > 0 ? <span>SES +{Math.round(sessionWon)}</span> : null}
              </div>
            ) : null}
            </div>

            {/* Slider popovers for circle controls */}
            {openSlider && (
              <>
                <button
                  type="button"
                  className="slots-slider-backdrop"
                  aria-label="Close"
                  onClick={() => setOpenSlider(null)}
                />
                <div
                  className="slots-slider-pop"
                  role="dialog"
                  aria-label={
                    openSlider === "bet" ? "Select bet" : "Select lines"
                  }
                >
                  <header>
                    <span>
                      {openSlider === "bet"
                        ? "Bet per line (GBUX)"
                        : "Paylines"}
                    </span>
                    <button
                      type="button"
                      onClick={() => setOpenSlider(null)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </header>
                  <div className="slots-chip-row">
                    {openSlider === "bet"
                      ? BET_PRESETS.map((b) => (
                          <button
                            key={b}
                            type="button"
                            className={`slots-chip ${
                              betPerLine === b ? "active" : ""
                            }`}
                            onClick={() => {
                              setBetPerLine(b);
                              setOpenSlider(null);
                            }}
                          >
                            {b}
                          </button>
                        ))
                      : LINE_PRESETS.map((l) => (
                          <button
                            key={l}
                            type="button"
                            className={`slots-chip ${
                              lines === l ? "active" : ""
                            }`}
                            onClick={() => {
                              setLines(l);
                              setOpenSlider(null);
                            }}
                          >
                            {l}
                          </button>
                        ))}
                  </div>
                  <p className="hint">
                    {openSlider === "bet"
                      ? `Total bet = ${totalBet} GBUX (${betPerLine} × ${lines} lines)`
                      : `${lines} lines · total bet ${totalBet} GBUX`}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {!walletAddress && (
          <div className="mt-2 rounded-xl border border-red-800/50 bg-red-950/40 px-3 py-2 text-center text-xs text-red-300">
            Connect wallet · play GBUX only.{" "}
            <button
              type="button"
              className="text-amber-300 underline"
              onClick={() => openWalletPanel("connect")}
            >
              Open wallet
            </button>
          </div>
        )}
        {walletAddress && (playBalance === 0 || playBalance === null) && !featureMode && (
          <div className="mt-2 rounded-xl border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-center text-xs text-amber-100/90">
            Play balance 0.{" "}
            <button
              type="button"
              className="text-amber-300 underline font-semibold"
              onClick={() => openWalletPanel("deposit")}
            >
              Transfer to play
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showJackpotWin && jackpotAwardsUi.length > 0 && (
          <JackpotWinOverlay
            awards={jackpotAwardsUi}
            total={jackpotWinUi}
            onDismiss={() => {
              setShowJackpotWin(false);
              if (autoSpin) setTimeout(triggerSpin, 300);
            }}
          />
        )}
        {showBonusTrigger && pendingBonusData && (
          <BonusTriggerOverlay
            spins={pendingBonusData.spins}
            multiplier={pendingBonusData.mult}
            onDismiss={() => {
              setShowBonusTrigger(false);
              setPendingBonusData(null);
              if (autoSpin) setTimeout(triggerSpin, 300);
            }}
          />
        )}
        {showSpecialTrigger && (
          <SpecialSpinOverlay
            onDismiss={() => {
              setShowSpecialTrigger(false);
              if (autoSpin) setTimeout(triggerSpin, 300);
            }}
          />
        )}
        {showFreeSpinTrigger && pendingFreeSpinData && (
          <FreeSpinTriggerOverlay
            spins={pendingFreeSpinData.spins}
            multiplier={pendingFreeSpinData.mult}
            onDismiss={() => {
              setShowFreeSpinTrigger(false);
              setPendingFreeSpinData(null);
              if (autoSpin) setTimeout(triggerSpin, 300);
            }}
          />
        )}
        {showBigWin && (
          <BigWinOverlay
            win={bigWinAmount}
            isJackpot={isJackpot}
            onDismiss={() => {
              setShowBigWin(false);
              if (autoSpin) setTimeout(triggerSpin, 300);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPayTable && (
          <SlotsInfoModal
            onClose={() => setShowPayTable(false)}
            onOpenNovel={() => {
              setShowPayTable(false);
              setShowWantedNovel(true);
            }}
          />
        )}
      </AnimatePresence>

      <WantedGraphicalNovel
        open={showWantedNovel}
        mode="story"
        onClose={() => setShowWantedNovel(false)}
      />

      {/*
        Wanted posters fly reel → crown counters (portal escapes machine transforms).
        Each spin: every jp_* on the landed grid flies to its Durin/Sylara/Kael/King badge.
      */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {wantedFlyers.map((f) => (
              <motion.div
                key={f.id}
                className="slots-wanted-flyer"
                initial={{
                  left: f.fromX,
                  top: f.fromY,
                  x: "-50%",
                  y: "-50%",
                  scale: 0.28,
                  opacity: 0.9,
                  rotate: -12,
                }}
                animate={{
                  left: f.toX,
                  top: f.toY,
                  x: "-50%",
                  y: "-50%",
                  scale: [0.28, 1.45, 0.42],
                  opacity: [0.9, 1, 0.12],
                  rotate: [-12, 8, 0],
                }}
                exit={{ opacity: 0, scale: 0.15 }}
                transition={{
                  duration: 0.9,
                  delay: f.delay,
                  ease: [0.22, 1, 0.36, 1],
                  scale: {
                    duration: 0.9,
                    delay: f.delay,
                    times: [0, 0.38, 1],
                    ease: [0.22, 1, 0.36, 1],
                  },
                }}
                onAnimationComplete={() => onWantedFlyerDone(f)}
              >
                <img
                  src={f.poster}
                  alt=""
                  draggable={false}
                  className="slots-wanted-flyer__img"
                />
              </motion.div>
            ))}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
}
